import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import window from 'ember-window-mock';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { apiBaseURL } from 'receivable-invoices-js/constants/hosts';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import { fetchApi } from 'receivable-invoices-js/utils/fetch-api';

interface RouteQueryResult {
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
}

export function useRouteQuery(): RouteQueryResult {
  const sentry = useSentry();
  const [searchParams] = useSearchParams();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Handle validation synchronously
  const validationError = useMemo(() => {
    if (!state) {
      return 'not_found';
    }
    return null;
  }, [state]);

  const {
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['mollie-activation', { code, state, error, errorDescription }],
    queryFn: async () => {
      const url = new URL('/v1/mollie/oauth/redirect', apiBaseURL);
      if (state) {
        url.searchParams.append('state', state);
      }

      if (code) {
        url.searchParams.append('code', code);
      }

      if (error) {
        url.searchParams.append('error', error);
      }

      if (errorDescription) {
        url.searchParams.append('error_description', errorDescription);
      }

      const response = await fetchApi(url.toString(), {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Error fetching Mollie activation');
      }

      const responseData = (await response.json()) as {
        data?: { attributes?: { origin?: string } };
      };

      const origin = responseData.data?.attributes?.origin;
      if (!origin) {
        throw new Error('Missing origin in response');
      }

      window.location.replace(origin);
    },
    enabled: !validationError && Boolean(state),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  if (queryError && shouldSendToSentry(queryError)) {
    sentry.captureException(queryError);
  }

  return {
    isLoading: validationError ? false : isLoading,
    error: validationError ?? queryError?.message ?? null,
    refetch,
  };
}
